import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useCalculatorAnalytics from '../hooks/useCalculatorAnalytics';
import BRAND from '../config/brand';
import CalculatorDiscoveryCta from './CalculatorDiscoveryCta';
import ServiceNextStep from './ServiceNextStep';
import { CONTENT_CATEGORIES } from '../config/planningOffers';
import './IncomeChangePaycheckTaxEstimator.css';
import './RothTraditionalCalculator.css';
import './SocialSecurityEstimator.css';

// 2025 Social Security parameters (educational approximation only)
const SS_WAGE_BASE = 176_100;
const BEND_POINT_1_AIME = 1_226;
const BEND_POINT_2_AIME = 7_391;
const FRA_AGE = 67;
const BREAKEVEN_CLAIM_AGES = [62, 67, 70];
const BREAKEVEN_CHART_START = 62;
const BREAKEVEN_CHART_END = 100;

const DEFAULTS = {
  filingStatus: 'single',
  currentAge: '55',
  annualIncome: '80000',
  knownFraBenefit: '',
  targetClaimAge: '67',
  spouseAge: '53',
  spouseIncome: '50000',
  spouseKnownFraBenefit: '',
  spouseClaimAge: '67',
};

const SSA_STATEMENT_URL = 'https://www.ssa.gov/myaccount/';

const DISCLOSURE =
  'Educational estimate only. This calculator uses simplified assumptions and is not an official Social Security Administration estimate. Actual benefits depend on your earnings record, inflation adjustments, claiming rules, taxes, Medicare premiums, and other factors.';

const EDUCATION_CARDS = [
  {
    title: 'Survivor Considerations',
    body: 'For many couples, the surviving spouse may continue receiving the larger Social Security benefit after one spouse passes away. Claiming decisions today can affect household income later in retirement.',
  },
  {
    title: 'Unexpected Disability',
    body: 'Many people focus on retirement while overlooking the financial impact of becoming unable to work before retirement age. Social Security Disability benefits follow different eligibility rules than retirement benefits. Learn more during a retirement planning review.',
  },
  {
    title: 'Healthcare Costs',
    body: 'Your retirement income can influence Medicare premiums. Withdrawals from retirement accounts and other income sources may affect what you pay for Medicare later in retirement.',
  },
];

const CTA_BULLETS = [
  'How much income your investments can provide',
  'Retirement taxes',
  'Roth vs. Traditional withdrawal strategies',
  'Medicare costs',
  'Retirement spending goals',
  'Household income planning for couples',
];
function formatUsdCompact(value) {
  if (value == null || !Number.isFinite(value)) return '--';
  const abs = Math.abs(value);
  if (abs >= 1000000) return `$${(value / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
  if (abs >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${Math.round(value)}`;
}

function formatUsd(value) {
  if (value == null || !Number.isFinite(value)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDiff(value) {
  if (value == null || !Number.isFinite(value)) return '--';
  const abs = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
  return value >= 0 ? `+${abs}` : `−${abs}`;
}

function parsePositiveNumber(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function parseOptionalNumber(value) {
  if (value === '' || value == null) return null;
  return parsePositiveNumber(value);
}

function resolveFraBenefit(income, knownFraMonthly) {
  const known = parseOptionalNumber(knownFraMonthly);
  if (known != null && known > 0) return Math.round(known);
  if (income == null) return null;
  return estimateFraBenefit(income);
}

function usesKnownFraBenefit(knownFraMonthly) {
  const known = parseOptionalNumber(knownFraMonthly);
  return known != null && known > 0;
}

function benefitSourceLabel(source) {
  return source === 'ssa_statement'
    ? 'Based on the FRA monthly benefit you entered from your SSA statement.'
    : 'Estimated from approximate income using simplified formulas.';
}

function parseClaimAge(value) {
  const age = parsePositiveNumber(value);
  if (age == null || age < 62 || age > 70) return null;
  return age;
}

export function estimateFraBenefit(annualIncome) {
  const capped = Math.min(annualIncome, SS_WAGE_BASE);
  const aime = capped / 12;
  let pia;
  if (aime <= BEND_POINT_1_AIME) {
    pia = aime * 0.9;
  } else if (aime <= BEND_POINT_2_AIME) {
    pia = BEND_POINT_1_AIME * 0.9 + (aime - BEND_POINT_1_AIME) * 0.32;
  } else {
    pia =
      BEND_POINT_1_AIME * 0.9 +
      (BEND_POINT_2_AIME - BEND_POINT_1_AIME) * 0.32 +
      (aime - BEND_POINT_2_AIME) * 0.15;
  }
  return Math.round(pia);
}

export function calculateBenefitAtAge(fraBenefit, fraAge, claimAge) {
  const monthDiff = Math.round((claimAge - fraAge) * 12);
  if (monthDiff === 0) return fraBenefit;
  if (monthDiff < 0) {
    const monthsEarly = Math.abs(monthDiff);
    const first36 = Math.min(monthsEarly, 36);
    const remaining = Math.max(monthsEarly - 36, 0);
    const reduction = first36 * (5 / 900) + remaining * (5 / 1200);
    return Math.round(fraBenefit * (1 - reduction));
  }
  return Math.round(fraBenefit * (1 + monthDiff * (2 / 300)));
}

/** Quick household SS estimate from salary — educational only, not an SSA estimate. */
export function estimateAnnualSocialSecurityQuick({
  primaryIncome = 0,
  spouseIncome = 0,
  retirementAge = FRA_AGE,
  spouseClaimAge,
  filingStatus = 'single',
}) {
  const claimAge = Math.min(70, Math.max(62, retirementAge));
  const spouseClaim = spouseClaimAge != null
    ? Math.min(70, Math.max(62, spouseClaimAge))
    : claimAge;
  const primaryMonthly = calculateBenefitAtAge(estimateFraBenefit(primaryIncome), FRA_AGE, claimAge);
  if (filingStatus === 'married' && spouseIncome > 0) {
    const spouseMonthly = calculateBenefitAtAge(estimateFraBenefit(spouseIncome), FRA_AGE, spouseClaim);
    return (primaryMonthly + spouseMonthly) * 12;
  }
  return primaryMonthly * 12;
}

function buildClaimingRows(fraBenefit, claimAge) {
  const rows = Array.from({ length: 9 }, (_, i) => {
    const age = 62 + i;
    const monthly = calculateBenefitAtAge(fraBenefit, FRA_AGE, age);
    return {
      age,
      monthly,
      vsFra: monthly - fraBenefit,
      annualized: monthly * 12,
      isChosen: age === claimAge,
      isFra: age === FRA_AGE,
    };
  });
  return {
    fraBenefit,
    rows,
    maxMonthly: Math.max(...rows.map((row) => row.monthly)),
    chosenRow: rows.find((row) => row.isChosen) ?? rows.find((row) => row.isFra),
  };
}

function buildPlanningInsight(claimAge) {
  if (claimAge < FRA_AGE) {
    return {
      title: 'Claiming Early',
      body: 'Claiming before full retirement age provides income sooner, but permanently reduces your monthly benefit. That trade-off can make sense if you need cash flow earlier — but it also means less guaranteed income for life.',
    };
  }
  if (claimAge > FRA_AGE) {
    return {
      title: 'Waiting Until 70',
      body: 'Delaying benefits increases your guaranteed monthly income for life. The trade-off is funding more years of retirement spending from other sources before Social Security begins.',
    };
  }
  return {
    title: 'Claiming at FRA',
    body: 'Claiming at full retirement age is a balanced starting point — you receive your unreduced benefit without waiting for delayed credits. Many households still need portfolio income, pensions, or other savings to close their retirement spending gap.',
  };
}

function findCrossoverAge(series, laterKey, earlierKey) {
  for (const row of series) {
    if (row[laterKey] > row[earlierKey]) {
      return row.age;
    }
  }
  return null;
}

export function buildBreakevenAnalysis(fraBenefit) {
  const monthlyByClaimAge = Object.fromEntries(
    BREAKEVEN_CLAIM_AGES.map((claimAge) => [
      claimAge,
      calculateBenefitAtAge(fraBenefit, FRA_AGE, claimAge),
    ]),
  );

  const series = [];
  for (let age = BREAKEVEN_CHART_START; age <= BREAKEVEN_CHART_END; age += 1) {
    const point = { age };
    BREAKEVEN_CLAIM_AGES.forEach((claimAge) => {
      const monthly = monthlyByClaimAge[claimAge];
      const yearsReceiving = age >= claimAge ? age - claimAge + 1 : 0;
      point[`cum${claimAge}`] = monthly * 12 * yearsReceiving;
      point[`monthly${claimAge}`] = monthly;
      point[`years${claimAge}`] = yearsReceiving;
    });
    series.push(point);
  }

  const crossovers = {
    age67vs62: findCrossoverAge(series, 'cum67', 'cum62'),
    age70vs67: findCrossoverAge(series, 'cum70', 'cum67'),
    age70vs62: findCrossoverAge(series, 'cum70', 'cum62'),
  };

  const summaryCards = [
    {
      title: 'Claiming at 62',
      body: crossovers.age67vs62
        ? `Provides more income earlier. Best cumulative outcome if lifespan is shorter than approximately age ${crossovers.age67vs62}.`
        : 'Provides more income earlier under these simplified assumptions.',
    },
    {
      title: 'Claiming at 67',
      body: crossovers.age67vs62 && crossovers.age70vs67
        ? `Balances earlier income with larger monthly checks. Catches claiming at 62 around age ${crossovers.age67vs62}; claiming at 70 catches up around age ${crossovers.age70vs67}.`
        : 'Balances earlier income with larger monthly checks at full retirement age.',
    },
    {
      title: 'Claiming at 70',
      body: crossovers.age70vs67
        ? `Produces the highest guaranteed monthly income. Generally provides greater lifetime income if living beyond approximately age ${crossovers.age70vs67}.`
        : 'Produces the highest guaranteed monthly income under these simplified assumptions.',
    },
  ];

  return {
    series,
    monthlyByClaimAge,
    crossovers,
    zones: {
      earlyEnd: crossovers.age67vs62,
      lateStart: crossovers.age70vs67,
    },
    summaryCards,
    yMax: Math.max(
      1,
      ...series.map((row) => Math.max(row.cum62, row.cum67, row.cum70)),
    ),
  };
}

function buildProjection(values) {
  const isMarried = values.filingStatus === 'married';
  const income = parseOptionalNumber(values.annualIncome);
  const claimAge = parseClaimAge(values.targetClaimAge);
  const primaryFraBenefit = resolveFraBenefit(income, values.knownFraBenefit);
  const primaryBenefitSource = usesKnownFraBenefit(values.knownFraBenefit)
    ? 'ssa_statement'
    : 'income_estimate';

  if (primaryFraBenefit == null || claimAge == null) return null;

  const primary = buildClaimingRows(primaryFraBenefit, claimAge);
  let spouse = null;

  if (isMarried) {
    const spouseIncome = parseOptionalNumber(values.spouseIncome);
    const spouseClaim = parseClaimAge(values.spouseClaimAge);
    const spouseFraBenefit = resolveFraBenefit(spouseIncome, values.spouseKnownFraBenefit);
    const spouseBenefitSource = usesKnownFraBenefit(values.spouseKnownFraBenefit)
      ? 'ssa_statement'
      : 'income_estimate';
    if (spouseFraBenefit == null || spouseClaim == null) return null;
    const spouseRows = buildClaimingRows(spouseFraBenefit, spouseClaim);
    spouse = {
      claimAge: spouseClaim,
      fraBenefit: spouseFraBenefit,
      benefitSource: spouseBenefitSource,
      monthly: spouseRows.chosenRow?.monthly ?? 0,
      annual: (spouseRows.chosenRow?.monthly ?? 0) * 12,
    };
  }

  const primaryMonthly = primary.chosenRow?.monthly ?? 0;
  const householdAnnual = primaryMonthly * 12 + (spouse?.annual ?? 0);
  const breakeven = buildBreakevenAnalysis(primaryFraBenefit);

  return {
    income,
    claimAge,
    fraBenefit: primaryFraBenefit,
    benefitSource: primaryBenefitSource,
    primaryMonthly,
    primaryAnnual: primaryMonthly * 12,
    spouse,
    householdAnnual,
    planningInsight: buildPlanningInsight(claimAge),
    breakeven,
    ...primary,
  };
}

export function socialSecurityClaimCategory(projection) {
  if (!projection) return undefined;
  if (projection.claimAge < FRA_AGE) return 'early_claiming';
  if (projection.claimAge > FRA_AGE) return 'delayed_claiming';
  return 'fra_claiming';
}

export default function SocialSecurityEstimator() {
  const {
    trackComplete,
    trackFieldInteraction,
    trackReset,
    trackResultView,
  } = useCalculatorAnalytics({
    calculatorId: 'social_security_estimator',
    calculatorName: 'social_security_projection',
  });

  const [values, setValues] = useState(DEFAULTS);
  const [hoveredBreakevenAge, setHoveredBreakevenAge] = useState(null);
  const isMarried = values.filingStatus === 'married';

  const projection = useMemo(() => buildProjection(values), [values]);

  useEffect(() => {
    if (!projection) return;
    const params = { result_category: socialSecurityClaimCategory(projection) };
    trackResultView(params);
    trackComplete(params);
  }, [projection, trackComplete, trackResultView]);

  const updateField = (analyticsField, stateKey) => (value) => {
    trackFieldInteraction(analyticsField);
    setValues((current) => ({ ...current, [stateKey]: value }));
  };

  const resetCalculator = () => {
    setValues(DEFAULTS);
    setHoveredBreakevenAge(null);
    trackReset();
  };

  return (
    <div className="pete-page">
      <header className="pete-header">
        <div className="pete-header-inner">
          <Link className="pete-logo-link" to="/links">
            <img
              src={BRAND.logoSrc}
              alt="Bautista Planning and Analytics"
              className="pete-logo-img"
              width={440}
              height={88}
              decoding="async"
            />
          </Link>
        </div>
      </header>

      <main className="pete-body">
        <div className="pete-inner">
          <div className="pete-title-block sse-hero">
            <h1>Social Security Planning Calculator</h1>
            <p className="pete-tax-year sse-hero-sub">
              A simple estimate to help you understand your direction — not every Social Security rule.
            </p>
            <p className="pete-social-calculator-note">
              Coming from TikTok, Reels, or Threads? Use this calculator, then compare your result to
              the decision you&apos;re actually trying to make.
            </p>
          </div>

          <div className="pete-disclaimer-banner" role="note">
            <strong>Educational estimate only.</strong> {DISCLOSURE}
          </div>

          <div className="sse-ssa-callout" role="note">
            <h3>Want your actual benefit numbers?</h3>
            <p>
              This calculator uses simplified estimates. For your official figures, sign in at{' '}
              <a href={SSA_STATEMENT_URL} target="_blank" rel="noopener noreferrer">
                SSA.gov
              </a>
              {' '}and view your Social Security Statement. If you already know your full retirement age
              monthly benefit, you can enter it below instead of using the income estimate.
            </p>
          </div>

          <div className="pete-card">
            <h2>Your Information</h2>
            <section className="sse-section">
              <h3>Filing status</h3>
              <div className="rtc-toggle" role="group" aria-label="Filing status">
                <button
                  type="button"
                  className={`rtc-toggle-btn ${!isMarried ? 'rtc-toggle-btn--active' : ''}`}
                  aria-pressed={!isMarried}
                  onClick={() => updateField('filing_status', 'filingStatus')('single')}
                >
                  Single
                </button>
                <button
                  type="button"
                  className={`rtc-toggle-btn ${isMarried ? 'rtc-toggle-btn--active' : ''}`}
                  aria-pressed={isMarried}
                  onClick={() => updateField('filing_status', 'filingStatus')('married')}
                >
                  Married
                </button>
              </div>
            </section>

            <section className="sse-section">
              <h3>{isMarried ? 'Primary person' : 'You'}</h3>
              <div className="sse-fields-grid">
                <Field
                  id="sse-current-age"
                  label="Current age"
                  value={values.currentAge}
                  onChange={updateField('current_age', 'currentAge')}
                />
                <Field
                  id="sse-annual-income"
                  label="Approximate annual income"
                  value={values.annualIncome}
                  onChange={updateField('annual_income', 'annualIncome')}
                  step="1000"
                  hint={values.knownFraBenefit
                    ? 'Optional when you enter your FRA monthly benefit below.'
                    : `Used to estimate benefits if you do not enter an FRA amount. Income above $${SS_WAGE_BASE.toLocaleString()} is capped.`}
                />
                <ClaimAgeField
                  id="sse-target-age"
                  label="Planned claiming age"
                  value={values.targetClaimAge}
                  onChange={updateField('target_claim_age', 'targetClaimAge')}
                />
              </div>
            </section>

            <section className="sse-section">
              <h3>Know your FRA benefit?</h3>
              <p className="sse-section-hint">
                Optional. Enter the monthly benefit at full retirement age (age {FRA_AGE}) from your
                SSA statement. If provided, this replaces the income-based estimate for{' '}
                {isMarried ? 'the primary person' : 'you'}.
              </p>
              <div className="sse-fields-grid">
                <Field
                  id="sse-known-fra"
                  label={isMarried ? 'Primary FRA monthly benefit (optional)' : 'FRA monthly benefit (optional)'}
                  value={values.knownFraBenefit}
                  onChange={updateField('known_fra_benefit', 'knownFraBenefit')}
                  step="50"
                  hint="From your SSA statement — the amount at full retirement age, before early/late adjustments."
                />
              </div>
            </section>

            {isMarried ? (
              <section className="sse-section">
                <h3>Spouse</h3>
                <div className="sse-fields-grid">
                  <Field
                    id="sse-spouse-age"
                    label="Current age"
                    value={values.spouseAge}
                    onChange={updateField('spouse_age', 'spouseAge')}
                  />
                  <Field
                    id="sse-spouse-income"
                    label="Approximate annual income"
                    value={values.spouseIncome}
                    onChange={updateField('spouse_income', 'spouseIncome')}
                    step="1000"
                    hint={values.spouseKnownFraBenefit
                      ? 'Optional when you enter the spouse FRA monthly benefit below.'
                      : 'Used to estimate benefits if no FRA amount is entered.'}
                  />
                  <ClaimAgeField
                    id="sse-spouse-claim"
                    label="Planned claiming age"
                    value={values.spouseClaimAge}
                    onChange={updateField('spouse_claim_age', 'spouseClaimAge')}
                  />
                </div>
                <div className="sse-fields-grid sse-spouse-fra-grid">
                  <Field
                    id="sse-spouse-known-fra"
                    label="Spouse FRA monthly benefit (optional)"
                    value={values.spouseKnownFraBenefit}
                    onChange={updateField('spouse_known_fra_benefit', 'spouseKnownFraBenefit')}
                    step="50"
                    hint="From the spouse's SSA statement at full retirement age."
                  />
                </div>
              </section>
            ) : null}

            <button className="pete-secondary-action" type="button" onClick={resetCalculator}>
              Reset calculator
            </button>
          </div>

          {projection ? (
            <>
              <div className="pete-card">
                <h2>What Will You Likely Receive?</h2>
                <p className="pete-summary-text">
                  {benefitSourceLabel(projection.benefitSource)}
                  {' '}Full retirement age is assumed to be {FRA_AGE} for this illustration.
                  {projection.spouse ? ` Spouse: ${benefitSourceLabel(projection.spouse.benefitSource)}` : ''}
                </p>
                <div className="sse-snapshot-grid">
                  <SnapshotCard
                    label="FRA monthly benefit (basis)"
                    value={`${formatUsd(projection.fraBenefit)}/mo`}
                    subtitle={projection.benefitSource === 'ssa_statement'
                      ? 'Entered from your SSA statement.'
                      : 'Estimated at full retirement age before claiming adjustments.'}
                  />
                  <SnapshotCard
                    label="Primary estimated benefit"
                    value={`${formatUsd(projection.primaryMonthly)}/mo`}
                    subtitle={`${formatUsd(projection.primaryAnnual)}/yr at age ${projection.claimAge}`}
                    accent
                  />
                  {projection.spouse ? (
                    <>
                      <SnapshotCard
                        label="Spouse FRA monthly benefit (basis)"
                        value={`${formatUsd(projection.spouse.fraBenefit)}/mo`}
                        subtitle={projection.spouse.benefitSource === 'ssa_statement'
                          ? 'Entered from SSA statement.'
                          : 'Estimated at full retirement age.'}
                      />
                      <SnapshotCard
                        label="Spouse estimated benefit"
                        value={`${formatUsd(projection.spouse.monthly)}/mo`}
                        subtitle={`${formatUsd(projection.spouse.annual)}/yr at age ${projection.spouse.claimAge}`}
                      />
                    </>
                  ) : null}
                  <SnapshotCard
                    label={isMarried ? 'Combined household Social Security' : 'Estimated annual retirement income'}
                    value={`${formatUsd(projection.householdAnnual)}/yr`}
                    subtitle={isMarried
                      ? 'Combined estimated benefits at your selected claiming ages.'
                      : 'Estimated Social Security income at your selected claiming age.'}
                    accent={isMarried}
                  />
                </div>
              </div>

              <div className="pete-card">
                <h2>Social Security Claiming Comparison</h2>
                <p className="pete-summary-text">
                  Compare estimated monthly benefits from age 62 through 70 using your primary FRA benefit
                  as the starting point. This helps illustrate the direction of your claiming decision —
                  not a lifetime optimization.
                </p>
                <BenefitChart rows={projection.rows} maxMonthly={projection.maxMonthly} />
                <div className="sse-table-wrap">
                  <table className="sse-table">
                    <thead>
                      <tr>
                        <th>Claiming age</th>
                        <th>Est. monthly</th>
                        <th>vs FRA</th>
                        <th>Annualized</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projection.rows.map((row) => (
                        <tr key={row.age} className={row.isChosen ? 'sse-row--chosen' : ''}>
                          <td>
                            {row.age}
                            {row.isFra ? <span className="sse-fra-badge">FRA</span> : null}
                            {row.isChosen && !row.isFra ? (
                              <span className="sse-chosen-badge">Selected</span>
                            ) : null}
                          </td>
                          <td>{formatUsd(row.monthly)}</td>
                          <td className={row.vsFra >= 0 ? 'sse-positive' : 'sse-negative'}>
                            {formatDiff(row.vsFra)}
                          </td>
                          <td>{formatUsd(row.annualized)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {projection.chosenRow ? (
                  <p className="pete-caveat">
                    At age <strong>{projection.chosenRow.age}</strong>, the estimate is approximately{' '}
                    <strong>{formatUsd(projection.chosenRow.monthly)}/month</strong>{' '}
                    ({formatUsd(projection.chosenRow.annualized)}/year).
                  </p>
                ) : null}
              </div>

              <div className="pete-card">
                <BreakevenAnalysisSection
                  breakeven={projection.breakeven}
                  hoveredAge={hoveredBreakevenAge}
                  onHover={setHoveredBreakevenAge}
                />
              </div>

              {isMarried && projection.spouse ? (
                <div className="pete-card">
                  <h2>What Changes If You&apos;re Married?</h2>
                  <p className="pete-summary-text">
                    Household retirement income often depends on two benefit streams — not just one claiming age.
                    This estimate adds each spouse&apos;s own benefit at the ages you selected.
                  </p>
                  <div className="sse-household-grid">
                    <div>
                      <span>Primary at age {projection.claimAge}</span>
                      <strong>{formatUsd(projection.primaryAnnual)}/yr</strong>
                    </div>
                    <div>
                      <span>Spouse at age {projection.spouse.claimAge}</span>
                      <strong>{formatUsd(projection.spouse.annual)}/yr</strong>
                    </div>
                    <div className="sse-household-total">
                      <span>Combined household Social Security</span>
                      <strong>{formatUsd(projection.householdAnnual)}/yr</strong>
                    </div>
                  </div>
                  <p className="pete-caveat">
                    Spousal, survivor, and divorced-spouse rules can change real-world outcomes.
                    This calculator intentionally stays simple — a comprehensive financial plan is where those pieces get connected.
                  </p>
                </div>
              ) : null}

              <div className="pete-card">
                <h2>How Does Social Security Fit Into Retirement Income?</h2>
                <p className="pete-summary-text">
                  Social Security is usually one layer of retirement income — not the whole plan.
                  Most households still need investments, employer plans, and other savings to fund their lifestyle.
                </p>
                <div className="sse-fit-callout">
                  <p>
                    If you want to see how Social Security compares to your broader retirement income goal,
                    try the{' '}
                    <Link to="/retirement-gap-estimator">Retirement Gap Estimator</Link>
                    {' '}next.
                  </p>
                </div>
              </div>

              <div className="pete-card">
                <div className="sse-insight-card" role="status">
                  <p className="sse-insight-label">Planning insight</p>
                  <h3>{projection.planningInsight.title}</h3>
                  <p>{projection.planningInsight.body}</p>
                </div>
              </div>

              <div className="pete-card">
                <h2>What Important Planning Issues Might You Be Missing?</h2>
                <p className="pete-summary-text">
                  Social Security interacts with taxes, retirement withdrawals, Medicare premiums, and household planning.
                  These topics are easy to overlook when focusing only on a claiming age.
                </p>
                <div className="sse-education-grid">
                  {EDUCATION_CARDS.map((card) => (
                    <div key={card.title} className="sse-education-card">
                      <h3>{card.title}</h3>
                      <p>{card.body}</p>
                    </div>
                  ))}
                </div>
              </div>

              <CalculatorDiscoveryCta
                eyebrow="Next Step"
                headline="Want help interpreting your result?"
                body={(
                  <>
                    <p>
                      Your claiming age affects more than one monthly number. A comprehensive financial plan can
                      connect this estimate to taxes, retirement withdrawals, Medicare, and household income.
                    </p>
                    <ul>
                      {CTA_BULLETS.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </>
                )}
                buttonId="social_security_estimator_financial_checkup_cta"
                secondaryButtonId="social_security_estimator_send_result_cta"
                calculatorId="social_security_estimator"
                calculatorName="social_security_projection"
                className="sse-cta"
              />
              <ServiceNextStep
                contentCategory={CONTENT_CATEGORIES.RETIREMENT}
                calculatorId="social_security_estimator"
                calculatorName="social_security_projection"
                promotionalAngle="social_security_calculator"
              />

              <p className="sse-footer-disclaimer">{DISCLOSURE}</p>
            </>
          ) : null}

          <p className="pete-footer-note">
            <Link className="pete-portal-link" to="/links">
              Back to planning links
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}

function Field({ id, label, value, onChange, step = '1', hint }) {
  return (
    <div className="pete-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint ? <p className="pete-hint">{hint}</p> : null}
    </div>
  );
}

function ClaimAgeField({ id, label, value, onChange }) {
  return (
    <div className="pete-field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        {Array.from({ length: 9 }, (_, i) => 62 + i).map((age) => (
          <option key={age} value={age}>{age}</option>
        ))}
      </select>
    </div>
  );
}

function SnapshotCard({ label, value, subtitle, accent = false }) {
  return (
    <div className={`sse-snapshot-card${accent ? ' sse-snapshot-card--accent' : ''}`}>
      <div className="sse-snapshot-label">{label}</div>
      <div className="sse-snapshot-value">{value}</div>
      <p className="sse-snapshot-sub">{subtitle}</p>
    </div>
  );
}

function BenefitChart({ rows, maxMonthly }) {
  return (
    <div className="sse-chart" aria-hidden="true">
      {rows.map((row) => (
        <div
          key={row.age}
          className={[
            'sse-bar-col',
            row.isChosen ? 'sse-bar-col--chosen' : '',
            row.isFra ? 'sse-bar-col--fra' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          title={`Age ${row.age}: ${formatUsd(row.monthly)}/mo`}
        >
          <div className="sse-bar-wrap">
            <div
              className="sse-bar"
              style={{ height: `${Math.round((row.monthly / maxMonthly) * 100)}px` }}
            />
          </div>
          <div className="sse-bar-val">{formatUsd(row.monthly)}</div>
          <div className="sse-bar-age">
            {row.age}
            {row.isFra ? <span className="sse-fra-tag">FRA</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

const BREAKEVEN_LINE_KEYS = [
  { claimAge: 62, key: 'cum62', className: 'sse-breakeven-line--62', label: 'Claim at 62' },
  { claimAge: 67, key: 'cum67', className: 'sse-breakeven-line--67', label: 'Claim at 67 (FRA)' },
  { claimAge: 70, key: 'cum70', className: 'sse-breakeven-line--70', label: 'Claim at 70' },
];

function BreakevenAnalysisSection({ breakeven, hoveredAge, onHover }) {
  const { crossovers, zones, summaryCards } = breakeven;

  return (
    <>
      <h2>Retirement Benefit Breakeven Analysis</h2>
      <p className="pete-summary-text">
        At what age does delaying Social Security actually pay off? This chart compares cumulative
        lifetime benefits for claiming at 62, 67, and 70 using your estimated primary benefit amounts.
        It is educational, not a prediction of how long you will live.
      </p>
      <BreakevenChart
        breakeven={breakeven}
        hoveredAge={hoveredAge}
        onHover={onHover}
      />
      {crossovers.age67vs62 ? (
        <div className="sse-breakeven-labels">
          <p>
            Claiming at <strong>67</strong> catches up to claiming at <strong>62</strong> around age{' '}
            <strong>{crossovers.age67vs62}</strong>.
          </p>
          {crossovers.age70vs67 ? (
            <p>
              Claiming at <strong>70</strong> catches up to claiming at <strong>67</strong> around age{' '}
              <strong>{crossovers.age70vs67}</strong>.
            </p>
          ) : null}
        </div>
      ) : null}
      {zones.earlyEnd && zones.lateStart ? (
        <div className="sse-zone-legend">
          <span><i className="sse-zone-swatch sse-zone-swatch--early" />Before age {zones.earlyEnd}: earlier claiming generally leads on cumulative dollars</span>
          <span><i className="sse-zone-swatch sse-zone-swatch--middle" />Ages {zones.earlyEnd}–{zones.lateStart}: breakeven zone where strategies converge</span>
          <span><i className="sse-zone-swatch sse-zone-swatch--late" />Beyond age {zones.lateStart}: delaying often leads on cumulative dollars</span>
        </div>
      ) : null}
      <div className="sse-breakeven-summary-grid">
        {summaryCards.map((card) => (
          <div key={card.title} className="sse-breakeven-summary-card">
            <h3>{card.title}</h3>
            <p>{card.body}</p>
          </div>
        ))}
      </div>
      <div className="sse-life-expectancy-box" role="note">
        <h3>Life Expectancy Is Only One Consideration</h3>
        <p>
          Choosing when to claim Social Security isn&apos;t only about maximizing lifetime dollars.
          Health, employment, retirement savings, taxes, survivor benefits, Medicare premiums, and
          household income needs can all influence the best claiming strategy. This calculator
          illustrates one piece of that decision.
        </p>
      </div>
      <div className="sse-breakeven-insight">
        <p className="sse-insight-label">Planning insight</p>
        <p>
          This analysis compares lifetime benefit totals using simplified assumptions. The best
          claiming strategy often depends on much more than life expectancy — including taxes,
          retirement withdrawals, healthcare costs, and household income needs. A comprehensive financial plan
          can help you interpret how Social Security fits into your complete retirement plan.
        </p>
      </div>
    </>
  );
}

function BreakevenChart({ breakeven, hoveredAge, onHover }) {
  const { series, crossovers, zones, yMax: rawMax } = breakeven;
  const width = 820;
  const height = 380;
  const pad = { left: 76, right: 36, top: 44, bottom: 56 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const startAge = BREAKEVEN_CHART_START;
  const endAge = BREAKEVEN_CHART_END;
  const yMax = Math.ceil(rawMax / 50000) * 50000;

  const x = (age) => pad.left + ((age - startAge) / (endAge - startAge)) * plotWidth;
  const y = (value) => pad.top + plotHeight - (value / yMax) * plotHeight;

  const buildPath = (key) => series
    .map((row, index) => `${index === 0 ? 'M' : 'L'} ${x(row.age).toFixed(1)} ${y(row[key]).toFixed(1)}`)
    .join(' ');

  const axisAges = [62, 70, 80, 90, 100].filter((age) => age >= startAge && age <= endAge);
  const hoveredPoint = series.find((row) => row.age === hoveredAge) || null;

  const handlePointer = (event) => {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const relativeX = ((event.clientX - rect.left) / rect.width) * width;
    const age = startAge + ((relativeX - pad.left) / plotWidth) * (endAge - startAge);
    const nearest = series.reduce((best, row) => (
      Math.abs(row.age - age) < Math.abs(best.age - age) ? row : best
    ), series[0]);
    onHover(nearest.age);
  };

  const crossoverMarkers = [
    { age: crossovers.age67vs62, label: '67 vs 62' },
    { age: crossovers.age70vs67, label: '70 vs 67' },
  ].filter((marker) => marker.age != null);

  return (
    <div className="sse-breakeven-chart-wrap">
      <div className="sse-breakeven-chart-container">
        {hoveredPoint ? (
          <div className="sse-breakeven-tooltip">
            <strong>Age {hoveredPoint.age}</strong>
            {BREAKEVEN_CLAIM_AGES.map((claimAge) => (
              <span key={claimAge}>
                Claim {claimAge}: {formatUsd(hoveredPoint[`cum${claimAge}`])} cumulative
                {' · '}{formatUsd(hoveredPoint[`monthly${claimAge}`])}/mo
                {' · '}{hoveredPoint[`years${claimAge}`]} yrs receiving
              </span>
            ))}
          </div>
        ) : null}
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Cumulative Social Security benefits by claiming age from 62 through 100"
          onMouseMove={handlePointer}
          onMouseLeave={() => onHover(null)}
        >
          {zones.earlyEnd && zones.lateStart ? (
            <>
              <rect
                className="sse-zone-shade sse-zone-shade--early"
                x={x(startAge)}
                y={pad.top}
                width={Math.max(0, x(zones.earlyEnd) - x(startAge))}
                height={plotHeight}
              />
              <rect
                className="sse-zone-shade sse-zone-shade--middle"
                x={x(zones.earlyEnd)}
                y={pad.top}
                width={Math.max(0, x(zones.lateStart) - x(zones.earlyEnd))}
                height={plotHeight}
              />
              <rect
                className="sse-zone-shade sse-zone-shade--late"
                x={x(zones.lateStart)}
                y={pad.top}
                width={Math.max(0, x(endAge) - x(zones.lateStart))}
                height={plotHeight}
              />
            </>
          ) : null}
          {[0, 0.25, 0.5, 0.75, 1].map((tick) => (
            <g key={`wealth-${tick}`}>
              <line
                className="sse-breakeven-grid"
                x1={pad.left}
                x2={width - pad.right}
                y1={y(yMax * tick)}
                y2={y(yMax * tick)}
              />
              <text className="sse-breakeven-axis" x={pad.left - 8} y={y(yMax * tick) + 4} textAnchor="end">
                {formatUsdCompact(yMax * tick)}
              </text>
            </g>
          ))}
          {axisAges.map((age) => (
            <text key={age} className="sse-breakeven-axis" x={x(age)} y={height - 14} textAnchor="middle">
              {age}
            </text>
          ))}
          <text className="sse-breakeven-axis" x={width / 2} y={height - 2} textAnchor="middle">Age</text>
          <text
            className="sse-breakeven-axis sse-breakeven-axis--ylabel"
            x={16}
            y={pad.top + plotHeight / 2}
            textAnchor="middle"
            transform={`rotate(-90 16 ${pad.top + plotHeight / 2})`}
          >
            Cumulative lifetime benefits
          </text>
          {crossoverMarkers.map((marker) => (
            <g key={marker.label}>
              <line
                className="sse-breakeven-crossover-line"
                x1={x(marker.age)}
                x2={x(marker.age)}
                y1={pad.top}
                y2={pad.top + plotHeight}
              />
              <text
                className="sse-breakeven-crossover-label"
                x={x(marker.age)}
                y={pad.top + 14}
                textAnchor="middle"
              >
                {marker.label} ~{marker.age}
              </text>
            </g>
          ))}
          {BREAKEVEN_LINE_KEYS.map((line) => (
            <path key={line.key} d={buildPath(line.key)} className={`sse-breakeven-line ${line.className}`} />
          ))}
          {hoveredPoint ? (
            BREAKEVEN_LINE_KEYS.map((line) => (
              <circle
                key={`dot-${line.key}`}
                className={`sse-breakeven-dot ${line.className}`}
                cx={x(hoveredPoint.age)}
                cy={y(hoveredPoint[line.key])}
                r={5}
              />
            ))
          ) : null}
        </svg>
      </div>
      <div className="sse-breakeven-legend">
        {BREAKEVEN_LINE_KEYS.map((line) => (
          <span key={line.key}>
            <i className={`sse-breakeven-swatch ${line.className}`} />
            {line.label}
          </span>
        ))}
      </div>
    </div>
  );
}
